<template>
  <v-modal @cancel="popUpClosed">
    <v-modal-header
      :modalName="user.creditorId ? 'Change Creditor' : 'Add Creditor'"
      @cancel="$emit('cancel')"
      slot="header"
    />
    <div slot="body">
      <div v-if="user.creditorId || selectedSupplier">
        <supplier-details v-if="selectedSupplier" :supplier="selectedSupplier"></supplier-details>
        <div class="spinner-container" v-if="!selectedSupplier">
          <b-spinner/>
        </div>
      </div>
      <v-search
        v-show="!hideSuppliersContent"
        :value="input"
        @input="debounceFilterUsersByInput((input = $event))"
        :label="'Search for suppliers:'"
        :placeholder="'Please type at least 3 letters to search (email or name or id)'"
      />
      <div class="spinner-container" v-show="!loadingSuppliersFeedback && isSuppliersLoading && !hideSuppliersContent">
        <b-spinner/>
      </div>
      <p v-if="loadingSuppliersFeedback && !hideSuppliersContent" :class="loadingSuppliersFeedback.styles">
        {{ loadingSuppliersFeedback.text }}
      </p>

      <v-modal-table :sizes="[1, 4, 3, 3, 1]" v-if="suppliers.length > 0 && !hideSuppliersContent" :columns="columns">
        <b-row class="align-items-center" v-for="supplier in suppliers" :key="supplier.id" slot="table-body">
          <v-grid-column :size="1" :label="'Creditor'">
            <span>{{ supplier.id || placeholder }}</span>
          </v-grid-column>

          <v-grid-column :size="4" :label="'E-mail'">
            <span>{{ supplier.email || placeholder }}</span>
          </v-grid-column>

          <v-grid-column :size="3" :label="'Name'">
            <span>{{ supplier.name || placeholder }}</span>
          </v-grid-column>

          <v-grid-column :size="3" :label="'Phone number'">
            <span>{{ supplier.phoneNumber || placeholder }}</span>
          </v-grid-column>

          <v-grid-column :size="1" :label="'Action'">
            <b-button variant="outline-primary" size="sm"
              @click="selectSupplier(supplier)"
              :disabled="selectedSupplier && supplier.id === selectedSupplier.id"
            >
              Match
            </b-button>
          </v-grid-column>
        </b-row>
      </v-modal-table>

      <p v-if="hideSuppliersContent && matchingFeedBack" :class="matchingFeedBack.styles">
        {{ matchingFeedBack.text }}
      </p>
    </div>
    <v-modal-footer slot="footer" @cancel="popUpClosed" isConfirmButtonCustom>
      <b-button variant="success" size="lg"
        slot="confirmButton"
        class="btn-offer"
        @click="matchUserToSupplier"
        v-if="isFailedToMatch === null"
        :disabled="!isConfirmMatchingEnabled"
      >
        {{ isMatching ? ' Matching ...' : 'Confirm' }}
        <b-spinner v-if="isMatching"/>
      </b-button>
    </v-modal-footer>
  </v-modal>
</template>

<script>
import VModal from '@/components/ui/VModal';
import VModalHeader from '@/components/ui/VModalHeader';
import { mapState, mapActions, mapMutations } from 'vuex';
import VModalTable from '@/components/ui/VModalTable';
import VSearch from '@/components/ui/VSearch';
import { GET_SUPPLIER_BY_CREDITOR_ID_ACTION, GET_SUPPLIERS_ACTION, GET_USER_ACTION } from '@/store/actionTypes';
import ApiLoadingStatus from '@/api/statusTypes';
import _ from 'lodash';
import { MATCH_USER_TO_SUPPLIER, SET_SELECTED_SUPPLIER, SET_SUPPLIERS } from '@/store/mutationTypes';
import SupplierDetails from '@/views/UsersView/components/SupplierModal/SupplierDetails';
import VGridColumn from '@/components/layout/VGridColumn';
import VModalFooter from '@/components/ui/VModalFooter';
import { userApi } from 'apiClient';

export default {
  name: 'SuppliersModal',
  components: {
    VModalTable,
    VModalHeader,
    VModal,
    VSearch,
    SupplierDetails,
    VGridColumn,
    VModalFooter,
  },
  props: {
    placeholder: {
      type: String,
      default: '__',
    },
    user: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      columns: ['Creditor', 'Email', 'Name', 'Phone Number', 'Action'],
      input: '',
      isSuppliersLoading: null,
      ApiLoadingStatus,
      isMatching: null,
      isFailedToMatch: null,
      hideSuppliersContent: null,
    };
  },
  created() {
    this.getSupplierByCreditorId(this.user.creditorId);
  },
  computed: {
    ...mapState({
      suppliers: (state) => state.suppliers.all,
      status: (state) => state.suppliers.status,
      selectedSupplier: (state) => state.suppliers.selectedSupplier,
    }),
    isInputValid() {
      return this.input.length > 2;
    },
    loadingSuppliersFeedback() {
      if (!this.isInputValid) {
        return {
          styles: ['text-info'],
          text: 'Please enter at least 3 letters to start the search.',
        };
      }
      if (this.isInputValid && this.suppliers.length === 0 && this.status === this.ApiLoadingStatus.STATUS_SUCCESSFUL) {
        this.resetSuppliers();
        return {
          styles: ['text-warning'],
          text: 'Sorry no data were found, change your input.',
        };
      } else if (this.isInputValid && this.status === this.ApiLoadingStatus.STATUS_ERROR_LOADING) {
        return {
          styles: ['text-danger'],
          text: 'Could not fetch data from the backend. Try again!',
        };
      }
      return null;
    },
    matchingFeedBack() {
      if (this.isMatching) {
        return {
          styles: ['text-info'],
          text: 'Matching Supplier loading ...',
        };
      }

      if (this.isFailedToMatch === true) {
        return {
          styles: ['text-danger'],
          text: 'Sorry we could not match the supplier. Error',
        };
      }

      if (!this.isMatching && this.isFailedToMatch === false) {
        return {
          styles: ['text-primary'],
          text: 'Supplier was successfully matched.',
        };
      }

      return null;
    },
    isConfirmMatchingEnabled() {
      return this.selectedSupplier && this.user.creditorId !== this.selectedSupplier.id;
    },
  },
  methods: {
    ...mapActions({
      getSuppliers: GET_SUPPLIERS_ACTION,
      getSupplierByCreditorId: GET_SUPPLIER_BY_CREDITOR_ID_ACTION,
      getUsers: GET_USER_ACTION,
    }),
    ...mapMutations({
      resetSuppliers: SET_SUPPLIERS,
      setSupplier: SET_SELECTED_SUPPLIER,
      matchUserToSupplierInStore: MATCH_USER_TO_SUPPLIER,
    }),
    debounceFilterUsersByInput: _.debounce(async function() {
      this.isSuppliersLoading = true;
      await this.getSuppliers(this.input);
      this.isSuppliersLoading = false;
    }, 1000),
    popUpClosed() {
      if (this.suppliers && this.suppliers.length > 0) {
        this.resetSuppliers();
      }
      this.setSupplier(null);
      this.$emit('cancel');
    },
    selectSupplier(selectedSupplier) {
      this.setSupplier(selectedSupplier);
    },
    async matchUserToSupplier() {
      try {
        if (!this.isConfirmMatchingEnabled) return;
        this.isMatching = true;
        this.hideSuppliersContent = true;
        this.matchUserToSupplierInStore({
          selectedUser: this.user,
          selectedSupplier: this.selectedSupplier,
        });
        await userApi.matchUserToSupplier(this.user, this.selectedSupplier);
        this.isFailedToMatch = false;
        this.isMatching = false;
      } catch (e) {
        this.isFailedToMatch = true;
        this.isMatching = false;
        this.getUsers();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.spinner-container {
  display: flex;
  width: 100%;
  justify-content: center;
  margin: 1em 0;

  i {
    font-size: 1.7em;
    color: darkslateblue;
  }
}

.search-container /deep/ .navbar {
  flex-grow: 1;

  div {
    width: 100%;

    input {
      width: 100%;
    }
  }
}

.modal-mask /deep/ .modal-body {
  margin: 0;
}

/deep/ .modal-container {
  max-width: 1200px;
}
</style>
